import { Capacitor } from '@capacitor/core';
import { PushNotifications, type Token, type PushNotificationSchema, type ActionPerformed } from '@capacitor/push-notifications';
import router from '@/router';
import { supabase } from './supabaseClient';
import { useNotificationStore } from '@/stores/useNotificationStore';

let initialized = false;

async function persistToken(token: string): Promise<void> {
  if (!token) return;
  const userId = (await supabase.auth.getSession()).data.session?.user?.id;
  if (!userId) return;

  const platform = Capacitor.getPlatform() === 'ios' ? 'apns' : 'fcm';
  const { error } = await supabase
    .from('profiles')
    .update({ push_token: token, push_platform: platform })
    .eq('id', userId);
  if (error) {
    console.error('[push] persist token failed', error);
  }
}

function handleReceived(notification: PushNotificationSchema): void {
  const kind = String((notification.data ?? {}).kind ?? '');
  if (kind === 'nearby_trade_alert') {
    // Realtime usually populates this first, but force refresh in case of latency.
    void useNotificationStore().loadNearbyPending().catch((e) => console.error('[push received refresh]', e));
    return;
  }
  if (kind === 'review_prompt') {
    void useNotificationStore().loadUnread().catch((e) => console.error('[push review refresh]', e));
  }
}

function handleAction(action: ActionPerformed): void {
  const data = action.notification?.data ?? {};
  const kind = String((data as any).kind ?? '');
  if (kind === 'nearby_trade_alert') {
    const alertId = String((data as any).alert_id ?? '');
    if (alertId) {
      void router.push(`/tabs/matches?nearby_alert_id=${encodeURIComponent(alertId)}`)
        .catch((e) => console.error('[push action push]', e));
    }
    return;
  }
  // Fallback for new_match / trade_* — deep-link by match_group_id if provided.
  if (kind === 'review_prompt') {
    const completedTradeId = String((data as any).completed_trade_id ?? '');
    if (completedTradeId) {
      void router.push(`/tabs/matches/history?tab=outgoing&highlight=${encodeURIComponent(completedTradeId)}`)
        .catch((e) => console.error('[push review action push]', e));
      return;
    }
  }
  const matchGroupId = String((data as any).match_group_id ?? '');
  if (matchGroupId) {
    void router.push(`/tabs/matches?highlight=${encodeURIComponent(matchGroupId)}`)
      .catch((e) => console.error('[push action push]', e));
  }
}

export const pushNotificationService = {
  async init(): Promise<void> {
    if (initialized) return;
    if (!Capacitor.isNativePlatform()) {
      initialized = true;
      return;
    }

    try {
      const perm = await PushNotifications.checkPermissions();
      let status = perm.receive;
      if (status === 'prompt' || status === 'prompt-with-rationale') {
        const req = await PushNotifications.requestPermissions();
        status = req.receive;
      }
      if (status !== 'granted') {
        console.warn('[push] permission not granted:', status);
        initialized = true;
        return;
      }

      await PushNotifications.addListener('registration', (token: Token) => {
        void persistToken(token.value).catch((e) => console.error('[push registration persist]', e));
      });

      await PushNotifications.addListener('registrationError', (err) => {
        console.error('[push registrationError]', err);
      });

      await PushNotifications.addListener('pushNotificationReceived', handleReceived);
      await PushNotifications.addListener('pushNotificationActionPerformed', handleAction);

      await PushNotifications.register();
      initialized = true;
    } catch (e) {
      console.error('[push init]', e);
    }
  },

  async clear(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    try {
      await PushNotifications.removeAllListeners();
    } catch (e) {
      console.error('[push clear]', e);
    }
    initialized = false;
  },
};
